import { Link, useLocation } from "react-router-dom";
import { saveBrowseReturn } from "@/lib/browsePosition";
import { imgUrl } from "@/lib/api";
import { PenNib, Certificate, ArrowsClockwise, Package, CreditCard } from "@phosphor-icons/react";
import LikeButton from "@/components/LikeButton";
import { FoundingHat } from "@/components/FoundingHat";

function badges(l) {
  const b = [];
  if (l.base) b.push({ label: "Base", cls: "" });
  if (l.insert) b.push({ label: "Insert", cls: "h57-chip-blue" });
  if (l.variation) b.push({ label: "Var", cls: "h57-chip-blue" });
  if (l.rookie) b.push({ label: "RC", cls: "h57-chip-orange" });
  if (l.autograph) b.push({ label: "Auto", cls: "h57-chip-gold" });
  if (l.relic) {
    const label = l.relic_type === "player_worn" ? "Player Worn" : l.relic_type === "game_used" ? "Game Used" : "Relic";
    b.push({ label, cls: "" });
  }
  // Serial number: show verbatim if it already contains a slash (e.g. "24/249"),
  // otherwise prefix with / (e.g. serial "249" → "/249"). Falls back to
  // "Numbered" when the flag is set but no serial value was captured.
  if (l.serial_number || l.numbered) {
    const raw = (l.serial_number || "").trim();
    const label = raw ? (raw.includes("/") ? raw : `/${raw}`) : "Numbered";
    b.push({ label, cls: "h57-chip-orange" });
  }
  if (l.ssp) b.push({ label: "SSP", cls: "h57-chip-gold" });
  if (l.sp) b.push({ label: "SP", cls: "h57-chip-gold" });
  if (l.graded) b.push({ label: `${l.grading_company || "GRD"} ${l.grade || ""}`.trim(), cls: "h57-chip-blue" });
  return b;
}

function CardPlaceholder() {
  return (
    <svg viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <defs>
        <linearGradient id="slabGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a1a1a" />
          <stop offset="100%" stopColor="#0a0a0a" />
        </linearGradient>
      </defs>
      {/* Slab outer */}
      <rect x="60" y="40" width="180" height="320" rx="4" fill="url(#slabGrad)" stroke="#3f3f46" strokeWidth="1.5" />
      {/* Slab top label bar */}
      <rect x="72" y="52" width="156" height="34" fill="#0a0a0a" stroke="#FF5F1F" strokeWidth="0.8" opacity="0.8" />
      <text x="150" y="74" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" letterSpacing="3" fill="#FF5F1F" fontWeight="700">HUNT57</text>
      {/* Card face */}
      <rect x="76" y="98" width="148" height="240" fill="#f5f5f0" opacity="0.06" stroke="#3f3f46" strokeWidth="1" />
      {/* Corner registration marks */}
      <path d="M84 106 L84 116 M84 106 L94 106" stroke="#FF5F1F" strokeWidth="1.2" opacity="0.5" fill="none" />
      <path d="M216 106 L216 116 M216 106 L206 106" stroke="#FF5F1F" strokeWidth="1.2" opacity="0.5" fill="none" />
      <path d="M84 330 L84 320 M84 330 L94 330" stroke="#FF5F1F" strokeWidth="1.2" opacity="0.5" fill="none" />
      <path d="M216 330 L216 320 M216 330 L206 330" stroke="#FF5F1F" strokeWidth="1.2" opacity="0.5" fill="none" />
      {/* Watermark */}
      <text x="150" y="230" textAnchor="middle" fontFamily="Chivo, sans-serif" fontSize="22" letterSpacing="4" fill="#27272a" fontWeight="900">HUNT57</text>
      <text x="150" y="252" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="7" letterSpacing="2.5" fill="#52525b" fontWeight="600">NO IMAGE YET</text>
    </svg>
  );
}

export default function ListingCard({ listing, liked = false }) {
  // 480px is the sweet spot for browse thumbnails at up to 2x DPI screens.
  const img = imgUrl(listing.front_image, { w: 480 });
  const cardBadges = badges(listing);
  const location = useLocation();
  // Only remember scroll position when the click originates on Browse; other
  // surfaces (My Cart, Vault, etc.) just navigate normally.
  const handleClick = () => {
    if (location.pathname === "/browse" || location.pathname.startsWith("/browse")) {
      saveBrowseReturn(listing.id);
    }
  };
  return (
    <Link
      to={`/listing/${listing.id}`}
      onClick={handleClick}
      data-testid={`listing-card-${listing.id}`}
      className="group h57-surface h57-surface-hover flex flex-col transition-transform duration-150 hover:-translate-y-1"
    >
      {/* Badge strip — lives above the image so nothing sits on the card art.
          min-h reserves the row even when a listing has zero badges, keeping
          all card heights aligned across the grid. */}
      <div className="min-h-[26px] px-2 pt-2 pb-1 flex flex-wrap gap-1 border-b border-white/5" data-testid={`card-badges-${listing.id}`}>
        {cardBadges.slice(0, 5).map((b, i) => (
          <span key={i} className={`h57-chip ${b.cls}`}>{b.label}</span>
        ))}
      </div>
      <div className="aspect-[3/4] bg-[#0A0A0A] relative overflow-hidden border-b border-white/5">
        {img ? (
          <img src={img} alt={listing.title} className="w-full h-full object-contain" loading="lazy" decoding="async" />
        ) : (
          <CardPlaceholder />
        )}
      </div>
      <div className="p-2 md:p-3 flex-1 flex flex-col gap-1">
        <div className="font-display font-bold text-xs md:text-sm leading-tight line-clamp-2 uppercase tracking-tight">
          {listing.title}
        </div>
        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div>
            <div className="font-mono text-[10px] text-zinc-500 uppercase flex items-center gap-1">
              @{listing.seller_username}
              <FoundingHat founding={listing.seller_founding_member} size={11} />
            </div>
            <div className="font-display font-black text-base md:text-lg text-[#FF5F1F]">
              ${Number(listing.price).toFixed(2)}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 min-w-0">
            <LikeButton
              listingId={listing.id}
              initialLiked={liked}
              initialCount={listing.likes_count || 0}
            />
            <div className="flex flex-wrap items-center justify-end gap-1 text-[10px] font-mono max-w-full">
              {listing.combined_shipping?.enabled && (
                <span
                  data-testid={`combined-ship-${listing.id}`}
                  title={`Combined shipping: $${Number(listing.combined_shipping.first).toFixed(2)} first + $${Number(listing.combined_shipping.addl).toFixed(2)}/card, max ${listing.combined_shipping.max}`}
                  className="inline-flex items-center gap-0.5 border border-[#007AFF] text-[#007AFF] px-1 py-0.5 uppercase tracking-widest text-[9px] font-bold leading-none whitespace-nowrap"
                >
                  <Package size={9} weight="bold" />
                  <span className="hidden sm:inline">Combo</span>
                </span>
              )}
              {listing.seller_pay_methods?.credit_card && (
                <span
                  data-testid={`accepts-cc-${listing.id}`}
                  title="Seller accepts credit card payments"
                  className="inline-flex items-center gap-0.5 border border-[#FF5F1F] text-[#FF5F1F] px-1 py-0.5 uppercase tracking-widest text-[9px] font-bold leading-none whitespace-nowrap"
                >
                  <CreditCard size={9} weight="bold" />
                  <span className="hidden sm:inline">CC</span>
                </span>
              )}
              {listing.autograph && <PenNib size={12} weight="bold" className="text-zinc-500 shrink-0" />}
              {listing.graded && <Certificate size={12} weight="bold" className="text-zinc-500 shrink-0" />}
              {listing.accepts_offers ? (
                <span
                  data-testid={`offers-yes-${listing.id}`}
                  title="Seller accepts offers"
                  className="inline-flex items-center gap-0.5 border border-emerald-500 text-emerald-400 px-1 py-0.5 uppercase tracking-widest text-[9px] font-bold leading-none whitespace-nowrap"
                >
                  <ArrowsClockwise size={9} weight="bold" />
                  <span className="hidden sm:inline">Offers</span>
                </span>
              ) : (
                <span
                  data-testid={`offers-firm-${listing.id}`}
                  title="Firm price — seller not accepting offers"
                  className="inline-flex items-center border border-red-500 text-red-400 px-1 py-0.5 uppercase tracking-widest text-[9px] font-bold leading-none whitespace-nowrap"
                >
                  Firm
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
